// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';

// Angular Material
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';

// Third party
import { takeUntil } from 'rxjs';

// Local library
import { AuthFacade } from '@amwager/authentication/data-access';
import {
  ACCOUNT_SETTINGS_COMING_SOON_MSG,
  AccountFacade,
  AccountSettingsCard,
  AccountSettingsService,
  ProfileService
} from '@amwager/account/data-access';
import {
  ENVIRONMENT,
  PermissionService
} from '@amwager/data-access';
import {
  MatNotificationService,
  TranslatePipe,
  UnsubscribeObservableService
} from '@amwager/util';

// Local file
import { WageringPreferencesComponent } from '../settings/wagering-preferences/wagering-preferences.component';
import { WageringLimitsComponent } from '../settings/wagering-limits/wagering-limits.component';
import { FavoriteTracksComponent } from '../settings/favorite-tracks/favorite-tracks.component';
import { LanguageComponent } from '../settings/language/language.component';

/**
 * Account settings rows (accordion + dark-mode toggle) inside profile panel
 */
@Component({
  selector: 'feature-mat-account-settings',
  templateUrl: './account-settings.component.html',
  styleUrls: ['./account-settings.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatExpansionModule,
    MatSlideToggleModule,
    WageringPreferencesComponent,
    WageringLimitsComponent,
    FavoriteTracksComponent,
    LanguageComponent,
    TranslatePipe
  ]
})
export class AccountSettingsComponent
  extends UnsubscribeObservableService
  implements OnInit, OnDestroy {

  readonly cards = this.accountSettingsService.cards;

  activeCard: AccountSettingsCard = null;

  darkMode = false;

  /**
   * App version shown below the settings list. Single source of truth is
   * AppComponent, which sets `window.version` at bootstrap — bump it there.
   */
  readonly appVersion = (window as Window & { version?: string }).version ?? '';

  constructor(
    private readonly accountSettingsService: AccountSettingsService,
    private readonly accountFacade: AccountFacade,
    private readonly authFacade: AuthFacade,
    private readonly profileService: ProfileService,
    private readonly permissionService: PermissionService,
    private readonly matNotificationService: MatNotificationService,
    private readonly cdRef: ChangeDetectorRef,
    @Inject(ENVIRONMENT) readonly environment: string
  ) {
    super();
  }

  ngOnInit(): void {
    this.darkMode = this.accountSettingsService.readPersistedDarkMode();
    this.accountSettingsService.applyDarkMode(this.darkMode);

    this.accountSettingsService.activeCard$
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((card) => {
        this.activeCard = card;
        this.cdRef.markForCheck();
      });

    this.accountSettingsService.darkMode$
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((mode) => {
        this.darkMode = mode;
        this.cdRef.markForCheck();
      });

    this.accountFacade.isGetAccountPreferencesSuccess$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((callback) => {
        if (callback?.isSuccess === undefined) return;

        const amWagerTheme = callback?.response?.AccountPreferences?.AmWagerTheme;
        if (amWagerTheme === undefined) return;

        const dark = this.accountSettingsService.themeFromPreference(amWagerTheme);
        this.accountSettingsService.applyDarkMode(dark);
      });

    this.authFacade.isLogoutSuccess$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((isLogout?: boolean) => {
        if (!isLogout) return;
        this.accountSettingsService.closeAllCards();
      });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  /**
   * Handler for expansion-panel (opened)/(closed) events
   * @param {Exclude<AccountSettingsCard, null>} cardId Card identifier
   * @param {boolean} expanded True if the panel was just opened
   * @returns {void}
   */
  onCardToggle(cardId: Exclude<AccountSettingsCard, null>, expanded: boolean): void {
    if (expanded) {
      this.accountSettingsService.toggleCard(cardId);
    } else if (this.activeCard === cardId) {
      this.accountSettingsService.closeAllCards();
    }
  }

  /**
   * Close Account Settings — collapse any open card and return
   * the profile panel to its default (Promotions) section.
   * @returns {void}
   */
  close(): void {
    this.accountSettingsService.closeAllCards();
    this.profileService.setActiveSection('default');
  }

  /**
   * Non-expandable row click — shows a "coming soon" alert
   * @returns {void}
   */
  openComingSoon(): void {
    this.showAlertInfo(ACCOUNT_SETTINGS_COMING_SOON_MSG);
  }

  /**
   * Confirm and dispatch the change-password logout flow
   * @returns {void}
   */
  changePasswordPrompt(): void {
    this.matNotificationService
      .showAlert(
        'Confirm',
        'Account Change Password. Confirm',
        '',
        'Change Password',
        'Cancel'
      )
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.profileService.closeProfile();
        this.authFacade.logout('change-password');
      });
  }

  /**
   * Toggle dark/light mode (syncs with API for logged-in users)
   * @param {MatSlideToggleChange} event Slide-toggle change event
   * @returns {void}
   */
  toggleDarkMode(event: MatSlideToggleChange): void {
    const mode = event.checked;

    if (this.permissionService.isAllowed()) {
      this.accountFacade.upsertAccountPrefenceFieldAPI(
        'AmWagerTheme',
        this.accountSettingsService.themeCodeFromMode(mode)
      );
    }

    this.accountSettingsService.applyDarkMode(mode);
  }

  /**
   * trackBy helper for the card list
   * @param {number} _index Loop index
   * @param {{ id: string }} card Card descriptor
   * @returns {string} Stable identity key
   */
  trackByCardId(_index: number, card: { id: string }): string {
    return card.id;
  }

  /**
   * True when this row should be hidden (another card is expanded)
   * @param {string} cardId Card identifier
   * @returns {boolean} True when the row should be hidden
   */
  isRowHidden(cardId: string): boolean {
    return this.activeCard !== null && this.activeCard !== cardId;
  }

  private showAlertInfo(message: string): void {
    this.matNotificationService.showAlert('Info', message, '', '', 'Close');
  }
}
